import { Box, Button, CloseButton, Dialog, DialogBody, DialogContent, DialogFooter, Drawer, Field, Flex, Portal } from '@chakra-ui/react';
import { FormInput, FormRadioButtons, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAddAnnouncement, useDeleteAnnouncement, useGetSingleAnnouncement, useUpdateAnnouncement } from '../../api/hooks/useAnnouncement';
import { Editor } from '@tinymce/tinymce-react';

type AnnouncementForm = {
	title: string;
	contentHtml: string;
	type: AnnouncementType;
};

export default function AnnouncementDrawer({ announcementID }: { announcementID?: number }) {
	const { t } = useTranslation();
	const { mutate: updateAnnouncement, isPending: isUpdatingAnnouncement } = useUpdateAnnouncement();
	const { mutate: addAnnouncement, isPending: isAddingAnnouncement } = useAddAnnouncement();

	const { closeDrawer } = useDrawer();
	const [isRemoveOpen, setIsRemoveOpen] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const { data: announcement, isFetching: isFetchingAnnouncement } = useGetSingleAnnouncement(announcementID as number, {
		isEnabled: !!announcementID,
	});

	const methods = useForm<AnnouncementForm>();

	const {
		setValue,
		handleSubmit,
		reset,
		control,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (announcement) {
			reset({
				...announcement,
			});
		}
	}, [announcement, reset]);

	function saveAnnouncement(values: AnnouncementForm): void {
		if (announcementID) {
			patchAnnouncement(values);
		} else {
			const payload: AddAnnouncementRequest = {
				title: values.title,
				contentHtml: values.contentHtml,
				type: values.type,
			};

			addAnnouncement(payload, {
				onSuccess: async () => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['announcements'] });
					queryClient.refetchQueries({ queryKey: ['announcement'] });

					closeDrawer('announcementDrawer');
				},
			});
		}
	}

	function patchAnnouncement(values: AnnouncementForm): void {
		if (!announcementID) {
			return;
		}

		type DirtyKeys = keyof AnnouncementForm;
		const payload: UpdateAnnouncementRequest = {
			announcementID,
			...Object.fromEntries((Object.keys(dirtyFields) as DirtyKeys[]).filter(key => values[key] !== undefined).map(key => [key, values[key]])),
		};

		updateAnnouncement(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['announcements'] });
				queryClient.refetchQueries({ queryKey: ['announcement'] });

				closeDrawer('announcementDrawer');
			},
		});
	}

	const title = useWatch({ control, name: 'title' });

	return isFetchingAnnouncement ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveAnnouncement)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{announcementID ? title : t('general.add_announcement')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<FormRadioButtons
							isRequired
							label={''}
							name="type"
							options={[
								{
									label: t('general.news'),
									value: 'news',
								},
								{
									label: t('general.change_log'),
									value: 'changeLog',
								},
							]}
						/>
						<FormInput isRequired label={t('general.title')} name="title" maxLength={200} />
						<Field.Root required={true}>
							<Field.Label>
								{t('general.message')}
								<Field.RequiredIndicator />
							</Field.Label>
						</Field.Root>
						<Box>
							<Editor
								id="tinymce-announcement-content"
								tinymceScriptSrc="/tinymce/tinymce.min.js"
								licenseKey="gpl"
								initialValue={announcement?.contentHtml}
								onEditorChange={content => {
									setValue('contentHtml', content, {
										shouldDirty: true,
									});
								}}
								init={{
									content_style: `
                    			p {
                       	 			margin: 0;
                    			}
                    			`,
									height: 500,
									plugins: 'link image table lists',
									contextmenu: 'link image table',
									promotion: false,
									menubar: false,
									toolbar: 'undo redo | blocks | bold italic underline | link image table | bullist numlist',
									block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3',
								}}
							/>
						</Box>
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					{announcementID ? (
						<>
							<RemoveAnnouncement announcementId={announcementID} isOpen={isRemoveOpen} setIsOpen={setIsRemoveOpen} />
							<Button id="removeAnnouncementButton" marginRight={'auto'} onClick={() => setIsRemoveOpen(true)} variant="subtle">
								{t('general.remove_from_account')}
							</Button>
						</>
					) : null}
					<Button onClick={() => closeDrawer('announcementDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingAnnouncement || isAddingAnnouncement || !isDirty}>
						{isUpdatingAnnouncement ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" onClick={() => closeDrawer('announcementDrawer')} />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}

function RemoveAnnouncement({ announcementId, isOpen, setIsOpen }: { announcementId: number; isOpen: boolean; setIsOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const { mutate, isPending: isDeletingAnnouncement } = useDeleteAnnouncement();
	const queryClient = useQueryClient();

	function removeAnnouncement(): void {
		mutate(announcementId, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.removed'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['announcements'] });
				closeDrawer('announcementDrawer');
			},
		});
	}

	return (
		<Dialog.Root open={isOpen} onOpenChange={e => setIsOpen(e.open)} placement="center">
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner pointerEvents="none">
					<DialogContent>
						<Dialog.Header>
							<Dialog.Title>{t('general.remove_announcement')}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<DialogBody>
							<p>{t('general.remove_announcement')}?</p>
						</DialogBody>
						<DialogFooter>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">{t('general.cancel')}</Button>
							</Dialog.ActionTrigger>
							<Button id="confirmRemoveAnnouncementButton" disabled={isDeletingAnnouncement} variant="subtle" onClick={removeAnnouncement}>
								{isDeletingAnnouncement ? t('general.please_wait') : t('general.remove')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
}
